import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import AuthService from "../../services/auth-services";
import { BASE_URL } from "../../services/api";
import styles from "./OwnerRegisterPage.module.css";
import SoftindexLogo from "../../assets/images/softindex-website-logo.webp";

export default function OwnerRegisterPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    confirmPassword: "",
    secretKey: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    isValid: false,
  });
  const [passwordMatch, setPasswordMatch] = useState({
    isMatching: false,
    hasValue: false,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuthStatus } = useAuth();

  useEffect(() => {
    // Check for registration success or errors from URL params
    const registrationStatus = searchParams.get("registration");
    const errorParam = searchParams.get("error");

    if (registrationStatus === "success") {
      // Registration successful, redirect to admin
      window.location.href = "/";
      return;
    }

    if (errorParam) {
      // For access denied errors, redirect to AccessDeniedPage
      if (
        errorParam === "invalid_secret_key" ||
        errorParam === "missing_secret_validation"
      ) {
        navigate(
          "/access-denied?reason=unauthorized&returnUrl=/owner-register"
        );
        return;
      }

      const errorMessages = {
        email_mismatch:
          "Your Google account email doesn't match the owner email address.",
        owner_already_exists:
          "Owner account already exists. Please use the login page.",
        registration_failed: "Registration failed. Please try again.",
      };
      setError(
        errorMessages[errorParam] || "An error occurred during registration."
      );
    }
  }, [searchParams, navigate]);

  // Password strength validation function
  const validatePasswordStrength = (password) => {
    const feedback = [];
    let score = 0;

    // Check length
    if (password.length < 8) {
      feedback.push("At least 8 characters");
    } else {
      score += 1;
    }

    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      feedback.push("One uppercase letter (A-Z)");
    } else {
      score += 1;
    }

    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      feedback.push("One lowercase letter (a-z)");
    } else {
      score += 1;
    }

    // Check for number
    if (!/\d/.test(password)) {
      feedback.push("One number (0-9)");
    } else {
      score += 1;
    }

    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(
      password
    );
    if (!hasSpecialChar) {
      feedback.push("One special character (!@#$%^&*)");
    } else {
      score += 1;
    }

    return {
      score,
      feedback,
      isValid: score >= 4 && password.length >= 8,
    };
  };

  // Check password matching
  const checkPasswordMatch = (password, confirmPassword) => {
    if (!confirmPassword) {
      return { isMatching: false, hasValue: false };
    }
    return {
      isMatching: password === confirmPassword,
      hasValue: true,
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate password strength when password changes
    if (name === "password") {
      const strength = validatePasswordStrength(value);
      setPasswordStrength(strength);

      // Also check password match if confirm password has a value
      if (formData.confirmPassword) {
        const match = checkPasswordMatch(value, formData.confirmPassword);
        setPasswordMatch(match);
      }
    }

    // Check password match when confirm password changes
    if (name === "confirmPassword") {
      const match = checkPasswordMatch(formData.password, value);
      setPasswordMatch(match);
    }
  };

  const handleOwnerRegister = async () => {
    if (
      !formData.email.trim() ||
      !formData.name.trim() ||
      !formData.password.trim() ||
      !formData.secretKey.trim()
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    if (!passwordStrength.isValid) {
      setError("Password does not meet strength requirements.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const registrationData = {
        email: formData.email.trim(),
        name: formData.name.trim(),
        password: formData.password,
        secretKey: formData.secretKey.trim(),
      };

      await AuthService.registerOwner(registrationData);

      await checkAuthStatus(true);

      window.location.href = "/?registration=success";
    } catch (err) {
      const errorMessage = err?.message || "";
      if (
        errorMessage.includes("Access denied") ||
        errorMessage.includes("Invalid secret key") ||
        errorMessage.includes("Secret key validation")
      ) {
        navigate(
          "/access-denied?reason=unauthorized&returnUrl=/owner-register"
        );
        return;
      }

      setError(
        errorMessage ||
          "Failed to complete owner registration. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    if (!formData.secretKey.trim()) {
      setError("Please enter the secret key before using Google registration.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${BASE_URL}/auth/store-owner-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          secretKey: formData.secretKey.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (
          errorData.message?.includes("Access denied") ||
          errorData.message?.includes("Invalid secret key") ||
          errorData.message?.includes("Secret key validation")
        ) {
          navigate(
            "/access-denied?reason=unauthorized&returnUrl=/owner-register"
          );
          setLoading(false);
          return;
        }

        throw new Error(errorData.message || "Failed to validate secret key");
      }

      await response.json().catch(() => ({}));

      window.location.href = `${BASE_URL}/auth/google/owner-register`;
    } catch (err) {
      setError(
        err.message ||
          "Failed to initiate Google owner registration. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <div className={styles.ownerRegisterPage}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <img
            src={SoftindexLogo || "/placeholder.svg"}
            alt="Softindex Logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>Owner Auth</h1>
          <p className={styles.subtitle}>
            Create your owner account to access the admin dashboard and manage
            the system.
          </p>

          {error && (
            <div className={styles.errorMessage}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.errorIcon}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <div className={styles.registerForm}>
            <div className={styles.formContainer}>
              <div className={styles.inputGroup}>
                <input
                  type="email"
                  name="email"
                  placeholder="Owner Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={styles.input}
                />

                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={styles.input}
                />

                <div className={styles.passwordInputContainer}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Create Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={styles.passwordInput}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles.passwordToggle}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>

                {formData.password && (
                  <div className={styles.passwordFeedback}>
                    <div className={styles.strengthIndicator}>
                      <div className={styles.strengthBar}>
                        <div
                          className={`${styles.strengthFill} ${
                            passwordStrength.score >= 1 ? styles.weak : ""
                          } ${
                            passwordStrength.score >= 3 ? styles.medium : ""
                          } ${
                            passwordStrength.score >= 4 ? styles.strong : ""
                          }`}
                          style={{
                            width: `${(passwordStrength.score / 5) * 100}%`,
                          }}
                        ></div>
                      </div>
                      <span
                        className={`${styles.strengthText} ${
                          passwordStrength.score >= 4
                            ? styles.strong
                            : passwordStrength.score >= 3
                            ? styles.medium
                            : styles.weak
                        }`}
                      >
                        {passwordStrength.score >= 4
                          ? "Strong"
                          : passwordStrength.score >= 3
                          ? "Medium"
                          : passwordStrength.score >= 1
                          ? "Weak"
                          : "Very Weak"}
                      </span>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <div className={styles.requirementsList}>
                        {passwordStrength.feedback.map((requirement, index) => (
                          <div key={index} className={styles.requirement}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className={styles.requirementIcon}
                            >
                              <circle cx="12" cy="12" r="10" />
                              <line x1="15" y1="9" x2="9" y2="15" />
                              <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                            <span>{requirement}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.passwordInputContainer}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={styles.passwordInput}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={styles.passwordToggle}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>

                {passwordMatch.hasValue && (
                  <div className={styles.passwordMatchFeedback}>
                    <div
                      className={`${styles.matchIndicator} ${
                        passwordMatch.isMatching ? styles.match : styles.noMatch
                      }`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={styles.matchIcon}
                      >
                        {passwordMatch.isMatching ? (
                          <>
                            <path d="M20 6L9 17l-5-5" />
                          </>
                        ) : (
                          <>
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                          </>
                        )}
                      </svg>
                      <span>
                        {passwordMatch.isMatching
                          ? "Passwords match"
                          : "Passwords do not match"}
                      </span>
                    </div>
                  </div>
                )}

                <div className={styles.passwordInputContainer}>
                  <input
                    type="password"
                    name="secretKey"
                    placeholder="Secret Key"
                    value={formData.secretKey}
                    onChange={handleInputChange}
                    className={styles.passwordInput}
                  />
                  <button
                    type="button"
                    className={styles.pasteButton}
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        setFormData((prev) => ({ ...prev, secretKey: text }));
                      } catch {
                        setError(
                          "Failed to paste from clipboard. Please try copying and pasting manually."
                        );
                      }
                    }}
                    tabIndex={-1}
                    title="Paste from clipboard"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 16 16"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      fill="#000000"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          fill="#444"
                          d="M13 4h-3v-4h-10v14h6v2h10v-9l-3-3zM3 1h4v1h-4v-1zM15 15h-8v-10h5v3h3v7zM13 7v-2l2 2h-2z"
                        ></path>
                      </g>
                    </svg>
                  </button>
                </div>

                <button
                  onClick={handleOwnerRegister}
                  disabled={
                    loading ||
                    !passwordStrength.isValid ||
                    !passwordMatch.isMatching ||
                    !formData.secretKey.trim()
                  }
                  className={styles.actionButton}
                >
                  {loading ? (
                    <div className={styles.spinner}></div>
                  ) : (
                    "Create Owner Account"
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={styles.divider}>
            <span>or</span>
          </div>

          <button
            onClick={handleGoogleRegister}
            disabled={loading}
            className={styles.googleButton}
          >
            <div className={styles.buttonContent}>
              {loading ? (
                <div className={styles.spinner}></div>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  className={styles.googleIcon}
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              <span className={styles.buttonText}>
                {loading ? "Registering..." : "Continue with Google"}
              </span>
            </div>
          </button>

          <div className={styles.footer}>
            <p className={styles.footerText}>
              By creating an owner account, you agree to our{" "}
              <a href="/terms" className={styles.footerLink}>
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" className={styles.footerLink}>
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
